import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useAppBar } from '../../providers/AppBarProvider';
import { fetchById, formatDate2 } from '../../utils/lib';
import { useTranslation } from 'react-i18next';
import { useConfig } from '../../providers/ConfigProvider';
import { useSnackbar } from '../../providers/SnackBarProvider';
import StoreIcon from "@mui/icons-material/Store";
import VetActionsBottomSheet from '../../layouts/VetActionsBottomSheet';

function ViewVet() {
  const [vet, setVet] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [cancelDialog, setCancelDialog] = useState({ open: false });
  const {updateTitle} = useAppBar();
  const {config, isLoadingConfig} = useConfig();
  const showSnackbar = useSnackbar();
  const { t } = useTranslation();

  useEffect(() => { 
    if (config) {
      setIsLoading(true);
      const fetchVet_ = async () => {
        const vet = await fetchById(config.vet.id, 'vets');
        setVet(vet);
        setIsLoading(false);
      };
      fetchVet_();
    }
  }, [config]);

  // Actualizar el title cuando cambie el vet
  useEffect(() => {
    if (vet) {
      updateTitle(vet.thumb_url || '', vet.name, vet.email, false, StoreIcon, handleAvatarClick);
    }
  }, [vet]);

  const handleAvatarClick = () => {
    if (vet?.photo_url) {
      window.open(vet.photo_url, '_blank');
    }
  };
  
  const handleCancelDialogOpen = () => {
    setCancelDialog({ open: true });
  };

  const handleCancelDialogClose = () => {
    setCancelDialog({ open: false });
  };

  const handleCancelDialogConfirm = async () => {
    try {
      const cancelUrl = import.meta.env.VITE_API_URL + "paypal?cancel&subscription_id=" + vet.subscription_id;
      const response = await fetch(cancelUrl);
      if (response.ok) {
        showSnackbar(t('view.subscriptionCancelledSuccess'), 'success');
        // Recargar datos del vet
        const updatedVet = await fetchById(vet.id, 'vets');
        updatedVet.subscription_id = null;
        setVet(updatedVet);
      } else {
        showSnackbar(t('view.subscriptionCancelError'), 'error');
      }
      handleCancelDialogClose();
    } catch (error) {
      //console.error('Error:', error);
      showSnackbar(t('view.subscriptionCancelError'), 'error');
      handleCancelDialogClose();
    }
  };

  const handleSubscription = () => {
    if (vet.subscription_id) {
      // Abrir diálogo de confirmación para cancelar
      handleCancelDialogOpen();
    } else {
      // Crear suscripción
      const idVet = vet.id;
      const country = vet.region?.country || '';
      const subscriptionUrl = import.meta.env.VITE_WEB_URL + "payments.php?id_vet=" + idVet + "&region=" + country;
      window.open(subscriptionUrl, '_blank');
    }
  };

  if (isLoading || isLoadingConfig) return (
    <>
      <CircularProgress
        size={42}
        sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-21px', marginLeft: '-21px' }}
      />
    </>
  );

  return (
    <Container
      maxWidth="xl"  
      style={{ overflow: 'auto', maxHeight: '100vh' }}>

      {/* Info Section */}
      <Box style={{ marginBottom: '16px' }}>
        <Typography variant="caption">{t('common.name')}</Typography>
        <Typography
          variant="body1"
          style={{ fontSize: '16px' }}
        >
          {vet.name}
        </Typography>
      </Box>

      {/* Text fields with optional visibility */}
      {vet.address && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.address')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {vet.address}
          </Typography>
        </Box>
      )}
      
      <Box style={{ marginBottom: '16px' }}>
        <Typography variant="caption">{t('common.region')}</Typography>
        <Typography
          variant="body1"
          style={{ fontSize: '16px' }}
        >
          {vet.region.friendly_name}
        </Typography>
      </Box>

      {vet.phone && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.phone')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {vet.phone}
          </Typography>
        </Box>
      )}

      <Box style={{ marginBottom: '16px' }}>
        <Typography variant="caption">{t('common.email')}</Typography>
        <Typography
          variant="body1"
          style={{ fontSize: '16px' }}
        >
          {vet.email}
        </Typography>
      </Box>

      {vet.web_page && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.webPage')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {vet.web_page}
          </Typography>
        </Box>
      )}

      {vet.notes && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.notes')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {vet.notes}
          </Typography>
        </Box>
      )}

      <Box style={{ marginBottom: '16px' }}>
        <Typography variant="caption">{t('view.plan')}</Typography>
        <Typography
          variant="body1"
          style={{ fontSize: '16px' }}
        >
          {vet.plan}
        </Typography>
      </Box>

      <Box style={{ marginBottom: '16px' }}>
        <Typography variant="caption">{t('view.monthlyUsage')}</Typography>
        <Typography
          variant="body1"
          style={{ fontSize: '16px' }}
        >
          {vet.usage}
        </Typography>
      </Box>

      <Box style={{ marginBottom: '16px' }}>
        <Typography variant="caption">{t('view.aiCredits')}</Typography>
        <Typography
          variant="body1"
          style={{ fontSize: '16px' }}
        >
          {vet.ai_credits.toFixed(7)}
        </Typography>
      </Box>

      <Box style={{ marginBottom: '16px' }}>
        <Typography variant="caption">{t('common.subscriptionUntil')}</Typography>
        <Typography
          variant="body1"
          style={{ fontSize: '16px' }}
        >
          {formatDate2(vet.subscription_until)}
        </Typography>
      </Box>

      <Box style={{ marginBottom: '16px' }}>
        <Button
          variant="contained"
          color={vet.subscription_id ? "error" : "primary"}
          onClick={handleSubscription}
          style={{ marginTop: '8px' }}
        >
          {vet.subscription_id ? t('view.cancelSubscription') : t('view.subscribe')}
        </Button>
      </Box>

      <Dialog
        open={cancelDialog.open}
        onClose={handleCancelDialogClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('view.cancelSubscriptionQuestion')}</DialogTitle>
        <DialogContent>
          {t('view.cancelSubscriptionWarning')}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialogClose}>{t('common.cancel')}</Button>
          <Button onClick={handleCancelDialogConfirm} variant="contained" color="error">
            {t('view.cancelSubscriptionDialogConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <VetActionsBottomSheet vet={vet} />

    </Container>
  );
}

export default ViewVet;